import type {
  AccessCreateRequest,
  AccessDelete,
  StorageUnit,
  StorageUnitCreate,
  StorageUnitUpdate,
  AccessRead,
  Status,
  StorageUnitType,
  Lease,
  LeaseType,
} from 'store/storageunits/types';
import { StorageUnitStatus } from 'store/storageunits/types';
import { get } from '../apiClient';
import { remove, post, put } from './storageMutations';

const VERSION = 'v1';
export const BASE_PATH = `cloud-save/${VERSION}`;

export async function getOne(id: string, signal?: AbortSignal) {
  return get<StorageUnit>(`${BASE_PATH}/storage-units/${id}`, { signal });
}

export enum ApiStorageUnitFilter {
  APPROVED = 'approved',
  DRAFT = 'draft',
  RECENT = 'recent',
  SHARED = 'shared',
  PRIVATE = 'private',
}

const allowedFilters = new Set<ApiStorageUnitFilter>(
  Object.values(ApiStorageUnitFilter),
);

const setParam = (
  params: URLSearchParams,
  key: string,
  value?: string | number | null,
) => {
  if (value === undefined || value === null) {
    return;
  }

  params.set(key, String(value));
};

const appendFilterParam = (
  params: URLSearchParams,
  filter?: ApiStorageUnitFilter | null,
) => {
  if (filter && allowedFilters.has(filter)) {
    params.set('filter', filter);
  }
};

const buildQueryString = (params: URLSearchParams) => {
  const query = params.toString();
  return query ? `?${query}` : '';
};

export interface GetStorageUnitsParams {
  parentId?: string;
  descendantId?: string;
  searchString?: string;
  limit?: number;
  offset?: number;
  filter?: ApiStorageUnitFilter;
  type?: StorageUnitType;
  country?: string;
  application?: string;
}

export async function getAll(
  {
    parentId,
    descendantId,
    searchString,
    limit,
    offset,
    filter,
    type,
    country,
    application,
  }: GetStorageUnitsParams,
  signal?: AbortSignal,
) {
  let query = '';

  if (parentId && !searchString) {
    const params = new URLSearchParams();
    setParam(params, 'parentId', parentId);
    setParam(params, 'limit', limit);
    setParam(params, 'offset', offset);
    appendFilterParam(params, filter);
    setParam(params, 'country', country);
    setParam(params, 'application', application);
    query = buildQueryString(params);
  } else if (descendantId) {
    const params = new URLSearchParams();
    setParam(params, 'descendantId', descendantId);
    query = buildQueryString(params);
  } else if (searchString) {
    const params = new URLSearchParams();
    if (parentId) {
      setParam(params, 'parentId', parentId);
    }
    setParam(params, 'searchString', searchString);
    setParam(params, 'limit', limit);
    setParam(params, 'offset', offset);
    appendFilterParam(params, filter);
    setParam(params, 'country', country);
    setParam(params, 'application', application);
    query = buildQueryString(params);
  } else {
    const params = new URLSearchParams();
    setParam(params, 'limit', limit);
    setParam(params, 'offset', offset);
    appendFilterParam(params, filter);
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    setParam(params, 'type', type || null);
    setParam(params, 'country', country);
    setParam(params, 'application', application);
    query = buildQueryString(params);
  }

  return get<StorageUnit[]>(`${BASE_PATH}/storage-units${query}`, { signal });
}

export async function create(body: StorageUnitCreate) {
  return post<StorageUnit>(`${BASE_PATH}/storage-units`, JSON.stringify(body));
}

export const EXPECTED_UPDATED_AT_VERIFIED_HEADER =
  'Expected-Updated-At-Verified';

export async function update(
  { id, ...rest }: StorageUnitUpdate,
  expectedUpdatedAt?: string,
) {
  return put<StorageUnit>(
    `${BASE_PATH}/storage-units/${id}`,
    JSON.stringify(expectedUpdatedAt ? { ...rest, expectedUpdatedAt } : rest),
  );
}

export async function removeOne(id: string) {
  return remove(`${BASE_PATH}/storage-units/${id}`);
}

export async function getAccess(id: string) {
  return get<AccessRead>(`${BASE_PATH}/storage-units/${id}/access`);
}

export async function grantAccess(id: string, body: AccessCreateRequest) {
  return post<StorageUnit>(
    `${BASE_PATH}/storage-units/${id}/access`,
    JSON.stringify(body),
  );
}

export async function removeAccess(id: string, body: AccessDelete) {
  return remove(
    `${BASE_PATH}/storage-units/${id}/access`,
    JSON.stringify(body),
  );
}

export async function acquireLease(id: string, leaseType: LeaseType) {
  return post<Lease>(
    `${BASE_PATH}/storage-units/${id}/lease`,
    JSON.stringify({ leaseType }),
  );
}

export async function releaseLease(id: string) {
  return remove<StorageUnit>(`${BASE_PATH}/storage-units/${id}/lease`);
}

export async function approveFile(id: string) {
  return post<Status>(`${BASE_PATH}/storage-units/${id}/status/approve`);
}

export async function unapproveFile(id: string) {
  return remove<Status>(`${BASE_PATH}/storage-units/${id}/status/approve`);
}

export const getByStatus = (status: StorageUnitStatus) => {
  return async (
    limit: number,
    offset: number,
    searchString?: string,
    signal?: AbortSignal,
  ) => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('offset', String(offset));

    if (searchString) {
      params.set('searchString', searchString);
    }

    return get<StorageUnit[]>(
      `${BASE_PATH}/storage-units/status/${status}?${params.toString()}`,
      { signal },
    );
  };
};

export const getApprovedFiles = getByStatus(StorageUnitStatus.APPROVED);

export const getTemplates = getByStatus(StorageUnitStatus.TEMPLATE);

export async function getArchived(
  limit: number,
  offset: number,
  signal?: AbortSignal,
) {
  const paginationInformation = `limit=${limit}&offset=${offset}`;
  return get<StorageUnit[]>(
    `${BASE_PATH}/storage-units?softDeleted=true&${paginationInformation}`,
    { signal },
  );
}

export async function archive(id: string) {
  return put<StorageUnit>(`${BASE_PATH}/storage-units/trashcan/${id}`);
}

export async function restore(id: string) {
  return remove(`${BASE_PATH}/storage-units/trashcan/${id}`);
}

export async function permanentlyDeleteArchived() {
  return remove(`${BASE_PATH}/storage-units/trashcan`);
}

export async function getProjects(signal?: AbortSignal) {
  return get<StorageUnit[]>(`${BASE_PATH}/storage-units/projects`, { signal });
}
